<script setup lang="ts">
defineProps<{
  /** `[label, value]` pairs; the value is text unless the `#value` slot renders it. */
  rows: Array<[string, string]>;
}>();

defineSlots<{
  /**
   * A row's value as markup — a badge, a link, an „ändern" button. Gets the row
   * and its index; render `row[1]` for the rows that stay plain text.
   */
  value?: (props: { row: [string, string]; index: number }) => unknown;
}>();
</script>

<template>
  <dl class="nx-settings-rows">
    <div v-for="(row, index) in rows" :key="row[0]" class="nx-settings-row">
      <dt>{{ row[0] }}</dt>
      <dd>
        <slot name="value" :row="row" :index="index">{{ row[1] }}</slot>
      </dd>
    </div>
  </dl>
</template>

<style scoped>
.nx-settings-rows {
  display: grid;
  gap: 0;
  margin: 0;
}

.nx-settings-row {
  display: grid;
  grid-template-columns: calc(var(--nxt-size-unit) * 10) minmax(0, 1fr);
  gap: var(--nxt-space-sm);
  min-width: 0;
  padding: var(--nxt-space-xs) 0;
  border-bottom: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-settings-row:last-child {
  border-bottom: 0;
}

/* Label recedes, value reads — the same pairing as NxKeyValueList. */
.nx-settings-row dt {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
}

.nx-settings-row dd {
  min-width: 0;
  margin: 0;
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-regular);
  overflow-wrap: anywhere;
}

@media (max-width: 35em) {
  .nx-settings-row {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--nxt-space-2xs);
  }
}
</style>
